'use client';

import { FC, useState, useRef } from 'react';
import { Content, isFilled } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import { registerContact } from '@/actions/hubspot/registerContact';
import cookies from 'js-cookie';

// HubSpot form configuration
const HUBSPOT_PORTAL_ID = '7119515';

// Field label → HubSpot internal name mapping.
// Keys are lowercased + trimmed labels (asterisks/colons stripped); values are
// the exact internal property names that HubSpot expects.
const FIELD_MAPPINGS: Record<string, string> = {
  // First name
  name: 'firstname',
  'first name': 'firstname',
  firstname: 'firstname',
  'full name': 'firstname',
  'your name': 'firstname',
  // Last name
  'last name': 'lastname',
  lastname: 'lastname',
  surname: 'lastname',
  // Email
  email: 'email',
  'email address': 'email',
  'your email': 'email',
  'work email': 'email',
  // Company
  company: 'company',
  'company name': 'company',
  'your company': 'company',
  organization: 'company',
  // Job title
  'job title': 'jobtitle',
  jobtitle: 'jobtitle',
  title: 'jobtitle',
  role: 'jobtitle',
  position: 'jobtitle',
  // Phone
  phone: 'phone',
  'phone number': 'phone',
  mobile: 'phone',
};

/**
 * Props for `LeadMagnetAiEbook`.
 */
export type LeadMagnetAiEbookProps =
  SliceComponentProps<Content.LeadMagnetAiEbookSlice>;

/**
 * Component for "Lead Magnet - AI Ebook" Slices.
 */
const LeadMagnetAiEbook: FC<LeadMagnetAiEbookProps> = ({ slice }) => {
  const isSidedLogo = slice.variation === 'sidedLogoGreenGradient';

  const {
    logo,
    background_image,
    label,
    headline,
    subheadline,
    form_title,
    form_fields,
    form_button_label,
    form_id,
    ebook_images,
  } = slice.primary;

  const description =
    'description' in slice.primary ? slice.primary.description : null;

  const [formData, setFormData] = useState<Record<string, string>>({});
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const sectionRef = useRef<HTMLElement>(null);

  const handleInputChange = (fieldLabel: string, value: string) => {
    setFormData((prev) => ({ ...prev, [fieldLabel]: value }));
    if (fieldErrors[fieldLabel]) {
      setFieldErrors((prev) => {
        const next = { ...prev };
        delete next[fieldLabel];
        return next;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setErrorMessage(null);
    setFieldErrors({});

    // Validate
    const newErrors: Record<string, string> = {};
    form_fields?.forEach((field) => {
      const val = formData[field.field_label || '']?.trim();
      if (field.required && !val) {
        newErrors[field.field_label || ''] = 'This field is required';
      }
      if (field.field_type === 'email' && val) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(val)) {
          newErrors[field.field_label || ''] = 'Please enter a valid email';
        }
      }
    });

    if (Object.keys(newErrors).length > 0) {
      setFieldErrors(newErrors);
      setIsSubmitting(false);
      return;
    }

    try {
      if (!form_id) {
        setErrorMessage('Form is not configured. Please contact support.');
        setIsSubmitting(false);
        return;
      }

      const hubspotProperties: Record<string, string> = {};
      Object.entries(formData).forEach(([lbl, value]) => {
        const key = lbl
          .toLowerCase()
          .replace(/[*:]/g, '')
          .replace(/\s+/g, ' ')
          .trim();
        const fieldName =
          FIELD_MAPPINGS[key] ||
          key.replace(/\s+/g, '_').replace(/[^a-z0-9_]/g, '');
        hubspotProperties[fieldName] = value;
      });

      const { postSubmitAction } = await registerContact({
        properties: hubspotProperties,
        pageUri: typeof window !== 'undefined' ? window.location.href : '',
        pageName: typeof document !== 'undefined' ? document.title : '',
        hubspotPortalIdForm: HUBSPOT_PORTAL_ID,
        hubspotGuidForm: form_id,
        hubspotutkCookie: cookies.get('hubspotutk'),
      });

      if (postSubmitAction?.type === 'redirect' && postSubmitAction.value) {
        window.location.href = postSubmitAction.value;
        return;
      }

      // Build thank-you redirect
      const currentUrl = new URL(window.location.href);
      const redirectParams = new URLSearchParams();
      [
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_term',
        'utm_content',
      ].forEach((p) => {
        const v = currentUrl.searchParams.get(p);
        if (v) {
          redirectParams.set(p, v);
        }
      });

      let headlineText = '';
      if (headline && headline.length > 0) {
        const first = headline[0];
        if (first && 'text' in first) {
          headlineText = (first as { text: string }).text;
        }
      }
      const downloadName = headlineText || document.title || 'ebook-download';
      redirectParams.set(
        'download',
        downloadName
          .toLowerCase()
          .replace(/[^a-z0-9]+/g, '-')
          .replace(/^-|-$/g, ''),
      );
      redirectParams.set('source_page', currentUrl.pathname);
      window.location.href = `/thank-you-for-your-download?${redirectParams.toString()}`;
    } catch (error) {
      console.error('Form submission error:', error);
      setErrorMessage('Something went wrong. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderForm = () => (
    <form
      onSubmit={handleSubmit}
      className={`flex flex-col ${isSidedLogo ? 'gap-6' : 'gap-4'}`}
    >
      {form_fields?.map((field, idx) => {
        const fieldLabel = field.field_label || `Field ${idx + 1}`;
        const fieldType = field.field_type === 'email' ? 'email' : 'text';

        return (
          <div key={idx} className="flex flex-col gap-2">
            <label
              className={`text-[16px] font-normal leading-[1.5] ${isSidedLogo ? 'text-white' : 'text-[#002726]'}`}
            >
              {fieldLabel}
              {field.required && <span className="ml-0.5">*</span>}
            </label>
            <input
              type={fieldType}
              value={formData[fieldLabel] || ''}
              onChange={(e) => handleInputChange(fieldLabel, e.target.value)}
              placeholder="Type here"
              className={`h-12 w-full rounded-lg px-3 py-2 text-[16px] outline-none transition-colors focus:ring-1 focus:ring-[#00F557] ${
                isSidedLogo
                  ? 'bg-[rgba(67,68,68,0.4)] text-white placeholder-[rgba(255,255,255,0.3)]'
                  : 'bg-[#F4F4F4] text-[#002726] placeholder-[rgba(0,0,0,0.3)]'
              } ${fieldErrors[fieldLabel] ? 'ring-1 ring-red-400' : ''}`}
            />
            {fieldErrors[fieldLabel] && (
              <p className="mt-1 text-xs text-red-500">
                {fieldErrors[fieldLabel]}
              </p>
            )}
          </div>
        );
      })}

      {errorMessage && <p className="text-sm text-red-500">{errorMessage}</p>}

      <button
        type="submit"
        disabled={isSubmitting}
        className="w-full rounded-full bg-[#00F557] px-6 py-3 font-manrope text-[16px] font-bold leading-[1.5] text-[#002726] transition-all hover:bg-[#00D964] disabled:cursor-not-allowed disabled:opacity-60"
      >
        {isSubmitting ? 'Submitting...' : form_button_label || 'Download Now'}
      </button>
    </form>
  );

  /* ── Sided Logo, Green Gradient variation ── */
  if (isSidedLogo) {
    return (
      <section
        ref={sectionRef}
        id="lead-magnet-ai-ebook"
        data-slice-type={slice.slice_type}
        data-slice-variation={slice.variation}
        className="relative w-full overflow-hidden bg-[#041118]"
      >
        {/* Background image */}
        {isFilled.image(background_image) && (
          <div className="absolute inset-0 z-0">
            <PrismicNextImage
              field={background_image}
              fill
              className="object-cover"
            />
          </div>
        )}

        {/* ── Top bar: logo left, badge right ── */}
        <div className="relative z-10 mx-auto flex max-w-[1380px] items-center justify-between px-5 py-[30px] md:px-[100px]">
          {isFilled.image(logo) ? (
            <PrismicNextImage field={logo} className="h-auto w-[129px]" />
          ) : (
            <div />
          )}

          {label && (
            <span
              className="inline-block rounded-[20px] border border-[rgba(255,255,255,0.7)] px-2 py-1 text-[14px] font-medium uppercase tracking-[3.22px] text-white backdrop-blur-[21px]"
              style={{
                backgroundImage:
                  'linear-gradient(138.55deg, rgba(255,255,255,0.47) 0%, rgba(255,255,255,0) 110.84%)',
              }}
            >
              {label}
            </span>
          )}
        </div>

        {/* ── Main content area ── */}
        <div className="relative z-10 mx-auto max-w-[1380px] px-5 pb-16 pt-[80px] md:px-[100px] md:pb-20">
          {/* Two-column hero + form */}
          <div className="flex flex-col gap-10 lg:flex-row lg:gap-[60px]">
            {/* Left column: text */}
            <div className="flex flex-1 flex-col items-start justify-center gap-10 py-0 lg:py-[50px]">
              {isFilled.richText(headline) && (
                <div className="font-inter text-[32px] font-extrabold leading-[1.2] text-white md:text-[42px] lg:text-[52px]">
                  <PrismicRichText field={headline} />
                </div>
              )}

              {isFilled.richText(subheadline) && (
                <div className="font-inter text-[16px] font-normal leading-[1.4] text-white md:text-[18px] [&_h4]:text-[16px] [&_h4]:font-normal md:[&_h4]:text-[18px]">
                  <PrismicRichText field={subheadline} />
                </div>
              )}
            </div>

            {/* Right column: glass form card */}
            <div className="flex flex-1 items-center">
              <div className="flex w-full flex-col gap-6 rounded-[16px] bg-[rgba(31,34,40,0.8)] p-6 opacity-90 shadow-[0px_4px_65px_0px_rgba(0,0,0,0.08)] backdrop-blur-[48px]">
                {form_title && (
                  <p className="font-inter text-[20px] font-medium leading-[1.2] text-white">
                    {form_title}
                  </p>
                )}

                {renderForm()}
              </div>
            </div>
          </div>

          {/* ── Bottom: ebook preview images ── */}
          {ebook_images && ebook_images.length > 0 && (
            <div className="mt-10 flex gap-4 md:mt-[40px]">
              {ebook_images.map((item, idx) => (
                <div
                  key={idx}
                  className="flex-1 overflow-hidden rounded-[16px]"
                >
                  {isFilled.image(item.image) && (
                    <PrismicNextImage
                      field={item.image}
                      className="h-auto w-full rounded-[16px] object-cover"
                    />
                  )}
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    );
  }

  /* ── Default variation ── */
  return (
    <section
      ref={sectionRef}
      id="lead-magnet-ai-ebook"
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="w-full"
    >
      {/* ── Logo bar ── */}
      {isFilled.image(logo) && (
        <div className="mx-auto flex max-w-[1380px] items-center justify-center px-5 py-[30px] md:px-[200px]">
          <PrismicNextImage field={logo} className="h-auto w-[129px]" />
        </div>
      )}

      {/* ── Main content card (not full-width) ── */}
      <div className="relative mx-auto max-w-[1380px] overflow-hidden rounded-[16px] bg-[#F5F5F0] px-5 pb-16 pt-10 md:px-[100px] md:pb-20 md:pt-[80px]">
        {/* Background image */}
        {isFilled.image(background_image) && (
          <div className="absolute inset-0 z-0">
            <PrismicNextImage
              field={background_image}
              fill
              className="object-cover"
            />
          </div>
        )}

        {/* Two-column hero + form */}
        <div className="relative z-10 mx-auto flex max-w-[1180px] flex-col gap-10 lg:flex-row lg:gap-[60px]">
          {/* ── Left column: text content ── */}
          <div className="flex flex-1 flex-col justify-center pt-0 lg:pt-[27px]">
            {/* Badge */}
            {label && (
              <div className="mb-4">
                <span className="inline-block rounded-full border border-[#041118] px-2 py-0.5 text-xs font-bold uppercase tracking-wider text-[#041118]">
                  {label}
                </span>
              </div>
            )}

            {/* Headline */}
            {isFilled.richText(headline) && (
              <div className="mb-4 font-inter text-[32px] font-extrabold leading-[1.2] text-[#002726] md:text-[42px] lg:text-[52px]">
                <PrismicRichText field={headline} />
              </div>
            )}

            {/* Subheadline */}
            {isFilled.richText(subheadline) && (
              <div className="mb-4 font-inter text-[18px] font-semibold leading-[1.4] text-[#002726] md:text-[22px]">
                <PrismicRichText field={subheadline} />
              </div>
            )}

            {/* Description */}
            {isFilled.richText(description) && (
              <div className="font-inter text-[16px] leading-[1.4] text-[#002726] md:text-[18px]">
                <PrismicRichText field={description} />
              </div>
            )}
          </div>

          {/* ── Right column: form card ── */}
          <div className="w-full shrink-0 lg:w-[530px]">
            <div className="rounded-[16px] bg-white p-6">
              {/* Form title */}
              {form_title && (
                <h3 className="mb-4 font-inter text-[20px] font-semibold leading-[1.2] text-[#002726] md:text-[22px]">
                  {form_title}
                </h3>
              )}

              {renderForm()}
            </div>
          </div>
        </div>

        {/* ── Bottom: ebook preview images ── */}
        {ebook_images && ebook_images.length > 0 && (
          <div className="relative z-10 mx-auto mt-10 flex max-w-[1180px] gap-4 md:mt-14">
            {ebook_images.map((item, idx) => (
              <div
                key={idx}
                className="flex-1 overflow-hidden rounded-[16px] bg-[#026846]"
              >
                {isFilled.image(item.image) && (
                  <PrismicNextImage
                    field={item.image}
                    className="h-auto w-full object-cover"
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default LeadMagnetAiEbook;
